import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ASSET_KINDS, type AssetKind, type AssetUpdateV1 } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import { BackLink, Banner, Field } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { TypeSelect } from './Assets.tsx'

/** Edit sends only the fields that changed, with the revision the device saw: the server merges field by field and refuses a
 *  change to a field somebody else changed meanwhile (REVISION_CONFLICT), which then shows up on the sync screen. */
export function AssetEditPage() {
  const { t } = useTranslation()
  const { assetId } = useParams({ from: '/assets/$assetId/edit' })
  const navigate = useNavigate()
  const { send } = useClub()
  const assets = useAssets()
  const access = useWriteAccess('asset.manage')
  const a = assets?.find((x) => x.id === assetId)
  const { busy, error, run } = useAction()
  const [f, setF] = useState<{ tag: string; kind: AssetKind; make: string; model: string; serial: string; year: string; acquired: string; type: string } | null>(null)
  const [tagError, setTagError] = useState<string | undefined>()
  const [unchanged, setUnchanged] = useState(false)
  useEffect(() => {
    if (a && !f) setF({ tag: a.readable_tag, kind: a.kind, make: a.make ?? '', model: a.model ?? '', serial: a.serial_no ?? '', year: a.year ? String(a.year) : '', acquired: a.acquired_on ?? '', type: a.asset_type_code ?? '' })
  }, [a, f])

  if (!assets) return <p>{t('common.loading')}</p>
  if (!a || !f) return <section className="flex flex-col gap-3"><p>{t('assets.empty')}</p><Link to="/assets" className="btn">{t('common.back')}</Link></section>
  if (!access.permitted) return <section className="flex flex-col gap-3"><Banner tone="warn">{t('assets.notAllowed')}</Banner><Link to="/assets/$assetId" params={{ assetId }} className="btn">{t('common.back')}</Link></section>
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s!, [k]: e.target.value }))

  function submit(e: FormEvent) {
    e.preventDefault(); setTagError(undefined); setUnchanged(false)
    void run(async () => {
      const text = (v: string) => (v.trim() ? v.trim() : null)
      const diff: AssetUpdateV1['set'] = {}
      const tag = f!.tag.trim()
      if (tag.toUpperCase() !== a!.readable_tag) diff.readable_tag = tag
      if (f!.kind !== a!.kind) diff.kind = f!.kind
      for (const [key, cur, next] of [['make', a!.make, text(f!.make)], ['model', a!.model, text(f!.model)], ['serial_no', a!.serial_no, text(f!.serial)], ['asset_type_code', a!.asset_type_code, text(f!.type)], ['acquired_on', a!.acquired_on, text(f!.acquired)]] as const) {
        if ((cur ?? null) !== next) diff[key] = next
      }
      const year = f!.year.trim() ? Number(f!.year) : null
      if ((a!.year ?? null) !== year) diff.year = year
      if (Object.keys(diff).length === 0) { setUnchanged(true); return }
      try {
        await send('asset.update@1', { asset_id: a!.id, expected_revision: a!.revision ?? 1, set: diff }, { aggregateId: a!.id })
      } catch (ex) {
        if (ex instanceof Error && 'issues' in ex && (ex as { issues: Array<{ path: unknown[] }> }).issues.some((i) => i.path.includes('readable_tag'))) { setTagError(t('assets.tagHelp')); return }
        throw ex
      }
      await navigate({ to: '/assets/$assetId', params: { assetId: a!.id } })
    })
  }

  const tagChanged = f.tag.trim().toUpperCase() !== a.readable_tag
  return (
    <section className="flex flex-col gap-4">
      <BackLink to="/assets/$assetId" params={{ assetId }}>← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('assets.editTitle', { tag: a.readable_tag })}</h1>
      {access.readOnlyMode && <Banner tone="warn">{t('platform.readOnly')}</Banner>}
      {a.lifecycle === 'retired' && <Banner tone="info">{t('assets.retiredBanner')}</Banner>}
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('assets.tag')} hint={t('assets.tagHelp')} error={tagError} htmlFor="tag">
          <input id="tag" className="input uppercase" required maxLength={32} autoCapitalize="characters" value={f.tag} onChange={set('tag')} />
        </Field>
        {tagChanged && <Banner tone="warn">{t('assets.tagChangeWarning')}</Banner>}
        <Field label={t('assets.kind')} htmlFor="kind">
          <select id="kind" className="input" value={f.kind} onChange={set('kind')}>{ASSET_KINDS.map((k) => <option key={k} value={k}>{t(`assets.kinds.${k}`)}</option>)}</select>
        </Field>
        <Field label={t('assets.assetType')} htmlFor="type">
          <TypeSelect id="type" value={f.type} onChange={(code) => setF((s) => ({ ...s!, type: code }))} testId="asset-type" />
        </Field>
        <Field label={t('assets.make')} htmlFor="make"><input id="make" className="input" maxLength={80} value={f.make} onChange={set('make')} /></Field>
        <Field label={t('assets.model')} htmlFor="model"><input id="model" className="input" maxLength={80} value={f.model} onChange={set('model')} /></Field>
        <Field label={t('assets.serial')} htmlFor="serial"><input id="serial" className="input" maxLength={80} value={f.serial} onChange={set('serial')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('assets.year')} htmlFor="year"><input id="year" className="input" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} value={f.year} onChange={set('year')} /></Field>
          <Field label={t('assets.acquired')} htmlFor="acquired"><input id="acquired" className="input" type="date" value={f.acquired} onChange={set('acquired')} /></Field>
        </div>
        {unchanged && <Banner tone="info">{t('assets.noChange')}</Banner>}
        {error && <Banner tone="error">{error}</Banner>}
        <button className="btn btn-primary" disabled={busy || access.readOnlyMode || a.lifecycle === 'retired'} data-testid="save-asset">{t('assets.save')}</button>
      </form>
    </section>
  )
}
